import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useParams} from 'react-router-dom'

import CheckIcon from '@mui/icons-material/Check'
import {Box, Typography, Theme} from '@mui/material'
import {getProductLineColors} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {useProductLine} from '@progyny/hooks/useProductLine'

export interface BookingSession {
  sessionIndex: string
  title: string
  isOptional?: boolean
  isBooked?: boolean
}

export interface BookingBreadcrumbProps {
  sessions: BookingSession[]
  activeSessionIndex: string
}

interface SessionBookingParams {
  program: string
  sessionid: string
}

const useStyles = makeStyles<{colors?: any}>()((theme: Theme, {colors}) => ({
  card: {
    padding: '25px 32px',
    background: '#FFFFFF',
    boxShadow: '0px 3px 16px rgba(190, 192, 198, 0.24)',
    borderRadius: '16px',
    width: '100%',
  },
  sessionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  sessionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  sessionIndexWrap: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    flexShrink: 0,
  },
  activeSessionIndex: {
    backgroundColor: colors?.primary || '#148199',
    color: '#FFFFFF',
  },
  inactiveSessionIndex: {
    backgroundColor: '#F3F4F6',
    color: '#7B7B86',
  },
  bookedSessionIndex: {
    backgroundColor: '#5CB3A3',
    color: '#FFFFFF',
  },
  sessionLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#251F1F',
    lineHeight: '20px',
  },
  sessionTitle: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#7B7B86',
    lineHeight: '16px',
    marginTop: '4px',
  },
  sessionLabelContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  optionalBadge: {
    padding: '6px 16px 4px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    backgroundColor: '#FFF3EC',
    color: '#E97E19',
  },
}))

export const BookingBreadcrumb: React.FC<BookingBreadcrumbProps> = ({
  sessions,
  activeSessionIndex,
}) => {
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles({colors})
  const {t} = useTranslation()
  const history = useHistory()
  const {program} = useParams<SessionBookingParams>()

  const handleSessionClick = (sessionIndex: string) => {
    const newUrl = `/consultations/program/${program}/book?session=${sessionIndex}`
    history.push(newUrl)
  }

  return (
    <Box className={classes.card}>
      <Box className={classes.sessionsContainer}>
        {sessions.map((session) => {
          const isActive = session.sessionIndex === activeSessionIndex
          const isBooked = session.isBooked

          const getSessionIndexClass = () => {
            if (isBooked) return classes.bookedSessionIndex
            if (isActive) return classes.activeSessionIndex
            return classes.inactiveSessionIndex
          }

          return (
            <Box
              key={session.sessionIndex}
              className={classes.sessionItem}
              onClick={() => handleSessionClick(session.sessionIndex)}
            >
              <Box
                className={`${classes.sessionIndexWrap} ${getSessionIndexClass()}`}
              >
                {isBooked ? (
                  <CheckIcon sx={{fontSize: '14px', color: '#FFFFFF'}} />
                ) : (
                  session.sessionIndex
                )}
              </Box>

              <Box>
                <Box className={classes.sessionLabelContainer}>
                  <Typography className={classes.sessionLabel}>
                    {t(
                      'consultations_session_booking_booking_breadcrumb_session'
                    )}{' '}
                    {session.sessionIndex}
                  </Typography>

                  {session.isOptional && (
                    <Typography className={classes.optionalBadge}>
                      {t('consultations_program_session_optional')}
                    </Typography>
                  )}
                </Box>

                <Typography className={classes.sessionTitle}>
                  {session.title}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
